import type { FC } from 'react';
import React, { memo, useMemo } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import type { PhotoArchProps } from './PhotoArch.types';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { LOW_RES_RENDITION, OptimizedEventGuestSitePhoto } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { addRendition } from '@shared/utils/photoRendition';
import { MAX_HEIGHT, ASPECT_RATIO, OUTER_PIECE_THICKNESS } from './PhotoArch.constants';
import { ArchPhoto } from './ArchPhoto';

export const PhotoArch: FC<PhotoArchProps> = memo(props => {
  const { photo, showSkeleton, innerArchColor = 'white', outerArchColor = 'mono14', showPlaceholder = false } = props;

  const [containerDimensions, containerRef] = useSizeObserver();

  const calculateBorderRadius = useMemo(() => {
    const maxHeight = photo && containerDimensions.height ? Math.min(MAX_HEIGHT, containerDimensions.height) : MAX_HEIGHT;
    const width = maxHeight * ASPECT_RATIO;
    return width / 2 + OUTER_PIECE_THICKNESS;
  }, [containerDimensions.height, photo]);

  return (
    <Flex
      width="100%"
      height="100%"
      maxHeight={MAX_HEIGHT}
      aspectRatio={`${ASPECT_RATIO}`}
      alignItems="center"
      justifyContent="center"
      ref={containerRef}
    >
      {!photo ? (
        showPlaceholder ? (
          <Box
            width="80%"
            height="100%"
            backgroundColor="lightgray"
            borderTopLeftRadius={pxToRem(calculateBorderRadius)}
            borderTopRightRadius={pxToRem(calculateBorderRadius)}
          />
        ) : null
      ) : (
        <SsrLazyScreen
          key={photo.id}
          src={addRendition({ url: photo.url, renditionSize: LOW_RES_RENDITION })}
          skeletonGroupOverrides={{
            Root: {
              props: {
                width: 'auto',
                height: '100%',
                maxHeight: MAX_HEIGHT,
                aspectRatio: `${ASPECT_RATIO}`,
              },
            },
            PlaceholderRoot: {
              props: {
                width: 'auto',
                height: '100%',
                maxHeight: MAX_HEIGHT,
                aspectRatio: `${ASPECT_RATIO}`,
              },
            },
          }}
          width="100%"
          height="100%"
          aspectRatio={`${ASPECT_RATIO}`}
          showSkeleton={showSkeleton}
        >
          {() => (
            <ArchPhoto
              photo={photo}
              containerDimensions={containerDimensions}
              calculateBorderRadius={calculateBorderRadius}
              outerArchColor={outerArchColor}
              innerArchColor={innerArchColor}
              photoRenderer={OptimizedEventGuestSitePhoto}
            />
          )}
        </SsrLazyScreen>
      )}
    </Flex>
  );
});

PhotoArch.displayName = 'PhotoArch';
